import { useEffect, useRef } from 'react';
import { EditorContent, Node, mergeAttributes, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Button } from '../ui';
import { PLACED_ATTR, SLOT_ATTR } from './articleHtml';

/**
 * PieceEditor — TipTap rich text over the piece's canonical html.
 *
 * Two custom atom nodes keep the image tooling intact through an edit:
 *  - imageSlot   ← <figure data-image-slot="key" data-status="…">   (queued marker)
 *  - placedImage ← <figure data-image="key"><img src alt></figure>  (chosen option)
 * Without them TipTap would drop the markers on the first keystroke.
 *
 * Saves are debounced (SAVE_MS) and only fire on real document changes.
 */

const SAVE_MS = 800;

const ImageSlotNode = Node.create({
  name: 'imageSlot',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      slotKey: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute(SLOT_ATTR) || '' },
      status: { default: 'queued', parseHTML: (el: HTMLElement) => el.getAttribute('data-status') || 'queued' },
    };
  },
  parseHTML() {
    return [{ tag: `figure[${SLOT_ATTR}]` }];
  },
  renderHTML({ node }) {
    return ['figure', { [SLOT_ATTR]: node.attrs.slotKey, 'data-status': node.attrs.status }];
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('figure');
      dom.className = 'ck-slot-node';
      dom.setAttribute('data-slot-key', node.attrs.slotKey);
      dom.setAttribute('contenteditable', 'false');
      dom.textContent = `Image slot · ${node.attrs.status} · pick or generate it in Preview`;
      return { dom };
    };
  },
});

const PlacedImageNode = Node.create({
  name: 'placedImage',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      slotKey: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute(PLACED_ATTR) || '' },
      src: { default: '', parseHTML: (el: HTMLElement) => el.querySelector('img')?.getAttribute('src') || '' },
      alt: { default: '', parseHTML: (el: HTMLElement) => el.querySelector('img')?.getAttribute('alt') || '' },
    };
  },
  parseHTML() {
    return [{ tag: `figure[${PLACED_ATTR}]` }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'figure',
      mergeAttributes(HTMLAttributes, { [PLACED_ATTR]: node.attrs.slotKey }),
      ['img', { src: node.attrs.src, alt: node.attrs.alt, loading: 'lazy' }],
    ];
  },
});

export interface PieceEditorProps {
  html: string;
  disabled?: boolean;
  onSave: (html: string) => void;
}

function ToolbarButton({
  editor,
  label,
  active,
  onClick,
}: {
  editor: Editor | null;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      variant={active ? 'secondary' : 'ghost'}
      disabled={!editor}
      onClick={onClick}
      style={{ minWidth: '32px', padding: '0 8px', fontWeight: active ? 600 : 500 }}
      aria-pressed={!!active}
    >
      {label}
    </Button>
  );
}

export default function PieceEditor({ html, disabled = false, onSave }: PieceEditorProps) {
  const timer = useRef<number | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const lastSaved = useRef(html);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Image.configure({ inline: false, allowBase64: false }),
      ImageSlotNode,
      PlacedImageNode,
    ],
    content: html,
    editorProps: {
      attributes: { class: 'ck-article ck-editor', 'aria-label': 'Article editor' },
    },
    onUpdate: ({ editor: ed }) => {
      // TipTap normalizes the stored html on load and reports it as an update; a read-only
      // (approved/published) piece must never write that back
      if (!ed.isEditable) return;
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const next = ed.getHTML();
        if (next === lastSaved.current) return;
        lastSaved.current = next;
        onSaveRef.current(next);
      }, SAVE_MS);
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Flush a pending save when leaving the editor.
  useEffect(() => {
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = null;
        const next = editor?.getHTML();
        if (next && next !== lastSaved.current) {
          lastSaved.current = next;
          onSaveRef.current(next);
        }
      }
    };
  }, [editor]);

  const setLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    // No native prompt() in Cockpit. Minimal flow: toggle a link off, or link the
    // selection to itself when the selected text is a URL. A proper link popover
    // is its own card.
    if (previous) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const { from, to } = editor.state.selection;
    const selected = editor.state.doc.textBetween(from, to, ' ').trim();
    if (/^https?:\/\//i.test(selected)) {
      editor.chain().focus().setLink({ href: selected }).run();
    }
  };

  return (
    <div className="ck-article-column">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          flexWrap: 'wrap',
          padding: '6px',
          marginBottom: '14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-raised)',
          position: 'sticky',
          // clear the status-bar strip in the installed PWA
          top: 'calc(var(--sat, 0px) + 12px)',
          zIndex: 5,
        }}
      >
        <ToolbarButton editor={editor} label="H2" active={editor?.isActive('heading', { level: 2 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton editor={editor} label="H3" active={editor?.isActive('heading', { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} />
        <span style={{ width: '1px', height: '18px', background: 'var(--border-hairline)', margin: '0 4px' }} />
        <ToolbarButton editor={editor} label="B" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} />
        <ToolbarButton editor={editor} label="I" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} />
        <ToolbarButton editor={editor} label="U" active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()} />
        <span style={{ width: '1px', height: '18px', background: 'var(--border-hairline)', margin: '0 4px' }} />
        <ToolbarButton editor={editor} label="• List" active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} />
        <ToolbarButton editor={editor} label="1. List" active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()} />
        <ToolbarButton editor={editor} label="Quote" active={editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()} />
        <ToolbarButton editor={editor} label="Link" active={editor?.isActive('link')} onClick={setLink} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', paddingRight: '6px' }}>
          {disabled ? 'Read only' : 'Saves as you type'}
        </span>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
